import { trackSize } from './media';

/**
 * Burning the webcam bubble into the recording.
 *
 * When a camera is on we draw the screen and the camera onto a canvas and
 * record `canvas.captureStream()`. Loom does the same thing, and it is the
 * only way the bubble survives into the file rather than living in a player
 * overlay nobody else can see.
 *
 * The subtle part is the draw clock. `requestAnimationFrame` is suspended in
 * background tabs, and a background tab is the *normal* state of a screen
 * recorder, since you are looking at whatever you are recording. So the loop
 * is driven by `requestVideoFrameCallback` on the screen-capture video
 * element, which is tied to the media pipeline rather than to page rendering
 * and keeps firing when the tab is hidden. rAF plus an interval is the
 * fallback for browsers without it.
 */

export type BubbleCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export const BUBBLE_CORNERS: readonly BubbleCorner[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];

interface FrameCallbackHost {
  requestVideoFrameCallback(callback: (now: number) => void): number;
  cancelVideoFrameCallback(handle: number): void;
}

function frameCallbackHost(video: HTMLVideoElement): FrameCallbackHost | null {
  const candidate = video as unknown as Partial<FrameCallbackHost>;
  return typeof candidate.requestVideoFrameCallback === 'function' &&
    typeof candidate.cancelVideoFrameCallback === 'function'
    ? (candidate as FrameCallbackHost)
    : null;
}

/** Off-DOM video elements do not reliably decode in Safari, so park it. */
function hiddenVideo(stream: MediaStream): HTMLVideoElement {
  const video = document.createElement('video');
  video.srcObject = stream;
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.setAttribute('aria-hidden', 'true');
  video.style.position = 'fixed';
  video.style.top = '0';
  video.style.left = '0';
  video.style.width = '1px';
  video.style.height = '1px';
  video.style.opacity = '0';
  video.style.pointerEvents = 'none';
  document.body.appendChild(video);
  void video.play().catch(() => {
    // Autoplay of a muted MediaStream is always allowed; if it somehow is not,
    // the draw loop simply paints the poster-black canvas.
  });
  return video;
}

function removeVideo(video: HTMLVideoElement | null): void {
  if (!video) return;
  video.pause();
  video.srcObject = null;
  video.remove();
}

export interface CompositorSettings {
  corner: BubbleCorner;
  /** Bubble diameter as a fraction of canvas height. */
  scale: number;
  visible: boolean;
  /** Selfie view: what people expect of their own face. */
  mirror: boolean;
}

export const DEFAULT_COMPOSITOR_SETTINGS: CompositorSettings = {
  corner: 'bottom-right',
  scale: 0.24,
  visible: true,
  mirror: true,
};

export class Compositor {
  readonly canvas: HTMLCanvasElement;

  private readonly context: CanvasRenderingContext2D;
  private readonly displayVideo: HTMLVideoElement;
  private cameraVideo: HTMLVideoElement | null = null;
  private settings: CompositorSettings = { ...DEFAULT_COMPOSITOR_SETTINGS };
  private frameHandle: number | null = null;
  private rafHandle: number | null = null;
  private intervalHandle: number | null = null;
  private canvasStream: MediaStream | null = null;
  private lastDrawAt = 0;
  private stopped = false;
  private readonly fps: number;

  constructor(display: MediaStream, camera: MediaStream | null, fps = 30) {
    this.fps = fps;
    const size = trackSize(display.getVideoTracks()[0]);

    this.canvas = document.createElement('canvas');
    // Even dimensions keep every encoder happy.
    this.canvas.width = Math.max(2, Math.round(size.width / 2) * 2);
    this.canvas.height = Math.max(2, Math.round(size.height / 2) * 2);

    const context = this.canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!context) throw new Error('This browser could not open a 2D canvas for the webcam overlay.');
    this.context = context;
    this.context.fillStyle = '#000000';
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.displayVideo = hiddenVideo(display);
    if (camera) this.cameraVideo = hiddenVideo(camera);

    this.startLoop();
  }

  /** Swap the camera without disturbing the screen capture. */
  setCamera(camera: MediaStream | null): void {
    removeVideo(this.cameraVideo);
    this.cameraVideo = camera ? hiddenVideo(camera) : null;
  }

  update(patch: Partial<CompositorSettings>): void {
    this.settings = { ...this.settings, ...patch };
  }

  get current(): CompositorSettings {
    return this.settings;
  }

  /** Cached: each call to canvas.captureStream() mints a fresh track. */
  captureStream(): MediaStream {
    if (!this.canvasStream) this.canvasStream = this.canvas.captureStream(this.fps);
    return this.canvasStream;
  }

  stop(): void {
    this.stopped = true;
    if (this.frameHandle !== null) {
      frameCallbackHost(this.displayVideo)?.cancelVideoFrameCallback(this.frameHandle);
      this.frameHandle = null;
    }
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    if (this.intervalHandle !== null) {
      window.clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
    for (const track of this.canvasStream?.getTracks() ?? []) track.stop();
    this.canvasStream = null;
    removeVideo(this.displayVideo);
    removeVideo(this.cameraVideo);
    this.cameraVideo = null;
  }

  private startLoop(): void {
    const host = frameCallbackHost(this.displayVideo);

    if (host) {
      const step = (): void => {
        if (this.stopped) return;
        this.draw();
        this.frameHandle = host.requestVideoFrameCallback(step);
      };
      this.frameHandle = host.requestVideoFrameCallback(step);
    } else {
      const step = (): void => {
        if (this.stopped) return;
        this.draw();
        this.rafHandle = requestAnimationFrame(step);
      };
      this.rafHandle = requestAnimationFrame(step);
    }

    // Safety net: keeps frames flowing if the primary clock stalls (a hidden
    // tab without requestVideoFrameCallback, a paused decode). Timers are
    // throttled in the background but never stopped outright.
    this.intervalHandle = window.setInterval(() => {
      if (this.stopped) return;
      if (performance.now() - this.lastDrawAt > 250) this.draw();
    }, 250);
  }

  private draw(): void {
    this.lastDrawAt = performance.now();
    const { width, height } = this.canvas;
    const context = this.context;

    if (this.displayVideo.readyState >= 2) {
      // The screen source can change size mid-recording (a resized window).
      // Letterbox rather than stretch: a squashed recording is unusable.
      drawContained(context, this.displayVideo, 0, 0, width, height);
    }

    const camera = this.cameraVideo;
    if (!camera || !this.settings.visible || camera.readyState < 2) return;

    const diameter = Math.round(height * this.settings.scale);
    const margin = Math.round(height * 0.035);
    const { corner } = this.settings;
    const x = corner === 'top-left' || corner === 'bottom-left' ? margin : width - margin - diameter;
    const y = corner === 'top-left' || corner === 'top-right' ? margin : height - margin - diameter;
    const radius = diameter / 2;

    context.save();
    context.beginPath();
    context.arc(x + radius, y + radius, radius, 0, Math.PI * 2);
    context.closePath();
    context.clip();
    context.fillStyle = '#0B0D10';
    context.fillRect(x, y, diameter, diameter);

    if (this.settings.mirror) {
      context.translate(x + diameter, y);
      context.scale(-1, 1);
      drawCovered(context, camera, 0, 0, diameter, diameter);
    } else {
      drawCovered(context, camera, x, y, diameter, diameter);
    }
    context.restore();

    // A hairline ring so the bubble reads as deliberate against light content.
    context.save();
    context.beginPath();
    context.arc(x + radius, y + radius, radius - 1, 0, Math.PI * 2);
    context.strokeStyle = 'rgba(232, 237, 242, 0.35)';
    context.lineWidth = 2;
    context.stroke();
    context.restore();
  }
}

/** `object-fit: contain`. The whole source, letterboxed. */
function drawContained(
  context: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  const sourceWidth = video.videoWidth || width;
  const sourceHeight = video.videoHeight || height;
  const scale = Math.min(width / sourceWidth, height / sourceHeight);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;

  if (drawWidth < width - 1 || drawHeight < height - 1) {
    context.fillStyle = '#000000';
    context.fillRect(x, y, width, height);
  }
  context.drawImage(
    video,
    x + (width - drawWidth) / 2,
    y + (height - drawHeight) / 2,
    drawWidth,
    drawHeight,
  );
}

/** `object-fit: cover`. Fills the circle, centre-cropped. */
function drawCovered(
  context: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  const sourceWidth = video.videoWidth || width;
  const sourceHeight = video.videoHeight || height;
  const scale = Math.max(width / sourceWidth, height / sourceHeight);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  context.drawImage(
    video,
    x + (width - drawWidth) / 2,
    y + (height - drawHeight) / 2,
    drawWidth,
    drawHeight,
  );
}

// ---------------------------------------------------------------------------
// Audio
// ---------------------------------------------------------------------------

export interface MixedAudio {
  /** Null when there is nothing to record: a silent screen capture. */
  track: MediaStreamTrack | null;
  close(): void;
}

/**
 * One audio track out of however many sources exist.
 *
 * With a single source we hand the track straight through: no AudioContext,
 * no resampling, no chance of the graph being suspended. With both a mic and
 * system audio we have to mix, because MediaRecorder records exactly one
 * audio track.
 */
export function mixAudio(streams: Array<MediaStream | null>): MixedAudio {
  const tracks = streams
    .filter((stream): stream is MediaStream => stream !== null)
    .flatMap((stream) => stream.getAudioTracks())
    .filter((track) => track.readyState === 'live');

  if (tracks.length === 0) return { track: null, close: () => {} };
  if (tracks.length === 1) return { track: tracks[0], close: () => {} };

  const context = new AudioContext();
  const destination = context.createMediaStreamDestination();

  for (const track of tracks) {
    const source = context.createMediaStreamSource(new MediaStream([track]));
    const gain = context.createGain();
    // Two full-volume sources sum past 0dBFS and clip. Pull both back a
    // little; the transcode normalises loudness downstream anyway.
    gain.gain.value = 0.85;
    source.connect(gain).connect(destination);
  }

  void context.resume().catch(() => {
    // Created inside a click handler, so this should already be running.
  });

  return {
    track: destination.stream.getAudioTracks()[0] ?? null,
    close: () => {
      void context.close().catch(() => {});
    },
  };
}
